import type { Layer, TagsManager } from 'dxf-writer'
import DatabaseObject from 'dxf-writer/src/DatabaseObject.js'

/**
 * DXF 多行文本（MTEXT）实体，支持超长文本分块与矩形宽度约束。
 */
export default class MText extends DatabaseObject {
  /** 所属图层 */
  layer!: Layer
  /** 线型名称，默认跟随图层 */
  lineType: string = 'ByLayer'
  /** 线宽，单位百分之毫米；-1 表示跟随图层 */
  lineWidth: number = -1
  /** 真彩色 RGB 整数，undefined 表示不写入 */
  trueColor: number | undefined

  /**
   * @param {number} x - x
   * @param {number} y - y
   * @param {number} height - Text height
   * @param {number} rotation - Text rotation
   * @param {string} value - the string itself
   * @param {number} rectWidth - width of the rectangle surrounding the text
   * @param {number} attachment - attachment point (combination of horizontal and vertical alignment)
   */
  constructor(
    public x: number,
    public y: number,
    public height: number,
    public rotation: number,
    public value: string,
    public rectWidth: number | undefined,
    public attachment: number
  ) {
    super(['AcDbEntity', 'AcDbMText'])
  }

  /**
   * 写入 MTEXT 组码序列。
   *
   * @param manager - DXF 标签写入器
   */
  tags(manager: TagsManager) {
    manager.push(0, 'MTEXT')
    super.tags(manager)
    manager.push(8, this.layer.name)
    manager.push(6, this.lineType)
    manager.push(62, 256)
    manager.push(370, this.lineWidth)
    if (this.trueColor !== undefined) {
      manager.push(420, this.trueColor)
    }
    manager.point(this.x, this.y)
    manager.push(40, this.height)
    if (this.rectWidth !== undefined) {
      manager.push(41, this.rectWidth)
    }
    manager.push(71, this.attachment)
    manager.push(50, this.rotation)
    this.handleLongMText(manager, this.value)
  }

  /**
   * 将长文本切分为符合 DXF 规范的组码数组
   * @param manager TagsManager 实例
   * @param text 原始长文本字符串
   */
  private handleLongMText(manager: TagsManager, text: string) {
    // AutoCAD DXF 规范中，MTEXT 的组码 1、组码 3最多允许 250 个字符（设置为 240，预留空间）
    const chunkSize = 240

    // 1. 处理换行符：CAD MTEXT 使用 \P 作为换行标记
    const dxfText = text.replace(/\r?\n/g, '\\P')

    let remaining = dxfText

    // 2. 循环切片，填充组码 3
    while (remaining.length > chunkSize) {
      // 确保不会在转义符 \P 中间切断 (简单处理：如果切点是 \，则往前挪一位)
      let actualSlice = chunkSize
      if (remaining[actualSlice - 1] === '\\') {
        actualSlice -= 1
      }

      manager.push(3, remaining.substring(0, actualSlice))
      remaining = remaining.substring(actualSlice)
    }

    // 3. 最后剩余的部分填充组码 1
    manager.push(1, remaining)
  }
}
